import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { ApplicationCard } from "@/components/admin/application/ApplicationCard";
import { Loader2 } from "lucide-react";
import { useState } from "react";
import { ApplicationViewSheet } from "@/components/admin/application/ApplicationViewSheet";
import type { Application } from "@/types/database.types";
import type { Profile } from "@/types/database.types";

interface UserApplicationsSheetProps {
  user: Profile | null;
  applications: Application[];
  isLoading: boolean;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onApplicationUpdated?: () => void;
}

export const UserApplicationsSheet = ({
  user,
  applications,
  isLoading,
  isOpen,
  onOpenChange,
  onApplicationUpdated
}: UserApplicationsSheetProps) => {
  const [selectedApplicationId, setSelectedApplicationId] = useState<string | null>(null);

  const handleViewApplication = (applicationId: string) => {
    setSelectedApplicationId(applicationId);
  };

  const handleCloseApplicationView = () => {
    setSelectedApplicationId(null);
  };

  const handleResetRequest = (applicationId: string) => {
    // TODO: Implement reset functionality if needed
    console.log('Reset requested for application:', applicationId);
  };

  const handleDeleteRequest = (applicationId: string) => {
    // TODO: Implement delete functionality if needed
    console.log('Delete requested for application:', applicationId);
  };

  const handleSubmit = (applicationId: string) => {
    // TODO: Implement submit functionality if needed
    console.log('Submit requested for application:', applicationId);
  };

  return (
    <>
      <Sheet open={isOpen} onOpenChange={onOpenChange}>
        <SheetContent 
          className="w-full sm:max-w-3xl overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <SheetHeader className="mb-4">
            <SheetTitle>
              {user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email : 'User'} - Applications
            </SheetTitle>
          </SheetHeader>

          {isLoading ? (
            <div className="flex items-center justify-center h-40">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : applications.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground">No applications found for this user</p>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Found {applications.length} application{applications.length !== 1 ? 's' : ''}
              </p>
              <div className="grid grid-cols-1 gap-4">
                {applications.map((application) => (
                  <ApplicationCard
                    key={application.id}
                    application={application}
                    onView={handleViewApplication}
                    onResetRequest={handleResetRequest}
                    onDeleteRequest={handleDeleteRequest}
                    onSubmit={handleSubmit}
                    isActionInProgress={false}
                  />
                ))}
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {selectedApplicationId && (
        <ApplicationViewSheet
          applicationId={selectedApplicationId}
          isOpen={true}
          onOpenChange={(open) => {
            if (!open) {
              handleCloseApplicationView();
            }
          }}
        />
      )}
    </>
  );
};